/****************
 * Product analytics on the web — the twin of the mobile module.
 *
 * The phone has emitted events since it shipped; the web emitted nothing, so
 * the admin dashboard would have shown a product used only on mobile and drawn
 * exactly the wrong conclusion from it.
 *
 * The three rules are the mobile module's, and they are its rules for good
 * reasons rather than house style:
 *
 * 1. ANALYTICS MUST NEVER BREAK THE APP. Every call is fire-and-forget. Nothing
 *    throws, nothing awaits, nothing blocks a render. A metrics pipeline that
 *    can take down a trading app is a bad trade at any price.
 * 2. IT MUST SURVIVE THE ENDPOINT BEING DOWN. A failure mutes sending for five
 *    minutes rather than retrying into a wall.
 * 3. IT SENDS EVENTS, NOT CONTENT. No balances, no keys, nothing from the
 *    broker. If a field would be awkward to show the user who generated it, it
 *    does not belong here.
 *
 * Consent is captured at sign-up.
 ****************/

import type { AnalyticsEvent } from './trackingPlan';
import { capturePostHog } from './posthog';

/** The closed set, from the tracking plan (trackingPlan.ts). The server
 *  stores only these names and counts anything else as rejected. */
export type { AnalyticsEvent };

/*
 * 5 s, not 10 (2026-10-07): the admin page's Live panel shows who is here
 * now, and the brief asks for an action to appear there within ten seconds.
 */
const FLUSH_AFTER_MS = 5_000;
const FLUSH_AT_COUNT = 20;
const COOL_OFF_MS = 5 * 60_000;
const MAX_QUEUE = 100;


/** An event's own details. Flat and small; never a key, a secret, a token, a
 *  balance or typed text (the server drops and redacts anything that looks
 *  like one, but the rule is not to send it). */
export type EventProps = Record<string, string | number | boolean | null | undefined | (string | number)[] | Record<string, string | number | boolean>>;

interface QueuedEvent {
  event: AnalyticsEvent;
  target?: string;
  duration_ms?: number;
  at: string;
  visit_id?: string;
  props?: EventProps;
}

let queue: QueuedEvent[] = [];
let timer: ReturnType<typeof setTimeout> | null = null;
let mutedUntil = 0;

function userId(): string | null {
  return (window as any).__clerk_user_id || null;
}

/****************
 * THE VISIT. 2026-10-07.
 *
 * One id per stretch of use: a new one after 30 minutes without an event,
 * kept in sessionStorage so a reload continues the visit and a new tab starts
 * its own. It is what lets the admin page say how long a visit lasted, how
 * many pages it had, and where it began and ended.
 ****************/
const VISIT_GAP_MS = 30 * 60_000;

function randomId(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  }
}

export function visitId(now = Date.now()): string {
  try {
    const raw = sessionStorage.getItem('cf_vid');
    const v = raw ? (JSON.parse(raw) as { id: string; last: number }) : null;
    const id = v && now - v.last < VISIT_GAP_MS ? v.id : randomId();
    sessionStorage.setItem('cf_vid', JSON.stringify({ id, last: now }));
    return id;
  } catch {
    return '';
  }
}

/** What the browser knows about itself, sent once per batch. */
function context() {
  try {
    return {
      locale: navigator.language,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      screen_w: window.screen?.width,
      screen_h: window.screen?.height,
      app_version: (import.meta as any).env?.VITE_BUILD_SHA || undefined,
    };
  } catch {
    return {};
  }
}

export function track(event: AnalyticsEvent, target?: string, durationMs?: number, props?: EventProps): void {
  try {
    // No user, no event: an anonymous row has nothing to report on and the
    // admin list is keyed by person.
    if (!userId()) return;
    if (Date.now() < mutedUntil) return;

    queue.push({
      event,
      target: target?.slice(0, 300),
      duration_ms: durationMs === undefined ? undefined : Math.round(durationMs),
      at: new Date().toISOString(),
      visit_id: visitId() || undefined,
      props,
    });

    // The same event in PostHog, so its funnels count what the admin page counts.
    capturePostHog(event, { ...(props || {}), target, duration_ms: durationMs });

    if (queue.length > MAX_QUEUE) queue = queue.slice(-MAX_QUEUE);
    if (queue.length >= FLUSH_AT_COUNT) { void flush(); return; }
    if (!timer) timer = setTimeout(() => void flush(), FLUSH_AFTER_MS);
  } catch {
    /* Analytics never surfaces an error. */
  }
}

async function flush(): Promise<void> {
  if (timer) { clearTimeout(timer); timer = null; }
  if (queue.length === 0) return;

  const batch = queue;
  queue = [];
  const id = userId();
  if (!id) return;

  try {
    const res = await fetch('/api/v1/events', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-User-ID': id },
      body: JSON.stringify({ events: batch, ctx: context() }),
      // The page may be closing as this fires. keepalive is what lets the last
      // batch survive a navigation, which is exactly the batch that says where
      // someone went before they left.
      keepalive: true,
    });
    if (!res.ok) mutedUntil = Date.now() + COOL_OFF_MS;
  } catch {
    mutedUntil = Date.now() + COOL_OFF_MS;
  }
}

/****************
 * ERRORS NOBODY CAUGHT. 2026-10-07.
 *
 * The brief's friction tab needs the errors people actually hit. The message
 * only (the server scrubs anything key-shaped from it), the route, and at
 * most five per page load so a render loop cannot flood the table.
 ****************/
let errorsSent = 0;
function reportError(message: unknown): void {
  if (errorsSent >= 5) return;
  const text = String((message as any)?.message ?? message ?? '').slice(0, 200);
  if (!text || /ResizeObserver loop/.test(text)) return;
  errorsSent += 1;
  track('client_error', text, undefined, { path: window.location.pathname });
}
if (typeof window !== 'undefined') {
  window.addEventListener('error', (e) => reportError(e.error ?? e.message));
  window.addEventListener('unhandledrejection', (e) => reportError(e.reason));
}

/****************
 * WHERE THEY CAME FROM. 2026-10-07.
 *
 * Read from the arrival URL and the referrer, kept for the length of the tab
 * only (sessionStorage: no cookie, nothing that follows the person across
 * visits before they have an account). The first touch is never overwritten;
 * the last touch is each arrival that carries a source. After sign-up the
 * app posts both once, and the server keeps the first post for the account.
 *
 * Only for a NEW account (created in the last day): an account made months
 * ago that signs in today did not come from today's link.
 *
 * Since 2026-10-31 index.html also keeps the arrival in a first-party cookie
 * (cf_arr) that the server reads and merges with this post; /m, which never
 * posts, is covered by that cookie alone (backend .../rest/provenance.go).
 ****************/
const CLICK_IDS = ['gclid', 'fbclid', 'ttclid', 'twclid', 'li_fat_id', 'rdt_cid', 'msclkid'];

interface Touch {
  utm_source?: string; utm_medium?: string; utm_campaign?: string; utm_content?: string; utm_term?: string;
  click_id_type?: string; referrer?: string; landing_path?: string; at: string;
}

/**
 * The channel link a person followed on copyfi.ai (copyfi.ai/tiktok, /ig, ...):
 * the marketing site sets cf_src=<channel>.<YYYYMMDD> on .copyfi.ai for 90 days
 * and redirects to the home page, so the visitor sees no label. Read here, it
 * names the source of an account that otherwise only shows the site as its referrer.
 * 2026-10-20, Mishka: "to track who comes from which link".
 */
export function channelFromCookie(cookie: string): { channel: string; day: string } | null {
  const m = /(?:^|;\s*)cf_src=([a-z0-9-]{1,32})\.(\d{8})(?:;|$)/.exec(cookie || '');
  return m ? { channel: m[1], day: m[2] } : null;
}

export function arrivalTouch(
  href: string,
  referrer: string,
  cookie: string = typeof document !== 'undefined' ? document.cookie : '',
): { touch: Touch; hasSource: boolean } {
  const url = new URL(href);
  const q = url.searchParams;
  const touch: Touch = { at: new Date().toISOString(), landing_path: url.pathname };
  for (const k of ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'] as const) {
    const v = q.get(k);
    if (v) touch[k] = v.slice(0, 128);
  }
  // ?src= is the short form of utm_source the plan of 2026-09-26 printed on
  // links, never read until 2026-10-31.
  if (!touch.utm_source && q.get('src')) touch.utm_source = q.get('src')!.slice(0, 64);
  // A utm_source in the URL always wins; the channel link only fills the gap.
  let fromChannel = false;
  if (!touch.utm_source) {
    const c = channelFromCookie(cookie);
    if (c) {
      touch.utm_source = c.channel;
      touch.utm_medium = 'channel_link';
      touch.utm_content = c.day;
      fromChannel = true;
    }
  }
  const click = CLICK_IDS.find((k) => q.has(k));
  if (click) touch.click_id_type = click;
  let external = false;
  try {
    if (referrer) {
      const r = new URL(referrer);
      external = r.host !== url.host;
      // Origin only: a referrer's query can carry someone else's search. An
      // Android app (android-app://com.whatsapp/) has no origin -- .origin is
      // "null" for that scheme, which filed WhatsApp and Gmail as "direct" --
      // so it is named by its package (2026-10-31).
      if (external) touch.referrer = r.protocol === 'android-app:' ? `android-app://${r.host}` : r.origin;
    }
  } catch { /* no referrer */ }
  return { touch, hasSource: !!(touch.utm_source || fromChannel || click || external) };
}

function remember(): void {
  try {
    const { touch, hasSource } = arrivalTouch(window.location.href, document.referrer);
    if (!sessionStorage.getItem('cf_ft')) sessionStorage.setItem('cf_ft', JSON.stringify(touch));
    if (hasSource || !sessionStorage.getItem('cf_lt')) sessionStorage.setItem('cf_lt', JSON.stringify(touch));
    const ref = new URL(window.location.href).searchParams.get('ref');
    if (ref) sessionStorage.setItem('cf_ref', ref.slice(0, 32));
  } catch { /* storage off: nothing to remember */ }
}
if (typeof window !== 'undefined') remember();

/** Called once the Clerk user is known. Posts the arrival for a new account. */
export function noteSignedIn(id: string, createdAt?: Date | null): void {
  try {
    if (!id || !createdAt || Date.now() - createdAt.getTime() > 24 * 3600_000) return;
    const flag = `cf_attr_sent:${id}`;
    if (localStorage.getItem(flag)) return;
    const read = (k: string) => { const v = sessionStorage.getItem(k); return v ? JSON.parse(v) : {}; };
    void fetch('/api/v1/me/attribution', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-User-ID': id },
      body: JSON.stringify({
        first: read('cf_ft'),
        last: read('cf_lt'),
        ref: sessionStorage.getItem('cf_ref') || '',
        ctx: context(),
      }),
      keepalive: true,
    }).then((r) => { if (r.ok) localStorage.setItem(flag, '1'); }).catch(() => {});
  } catch { /* never fatal */ }
}

/****************
 * TIME ON A SCREEN, INCLUDING TIME NOBODY NAVIGATED AWAY FROM.
 *
 * Guillaume, 2026-09-07: "the tracker does not seem like working, if we sum all
 * rows it's as if he spent 6-7 minutes on the website but he is always on it."
 *
 * He was right, and the reason is that this only ever reported on UNMOUNT. Sit
 * on /sessions for an hour and then close the tab and it recorded nothing: the
 * effect cleanup never ran, and the `pagehide` handler below flushed the QUEUE
 * without stopping the running clock. The only time that counted was time
 * belonging to a screen somebody navigated away from, in-app, before leaving --
 * which is a small and biased fraction of the real number.
 *
 * Three things close that:
 *
 *   1. A CHECKPOINT every minute. Long stays are now recorded as they happen
 *      instead of hanging on a single event that may never fire. Also bounds
 *      the loss from a crash or a killed tab to under a minute.
 *   2. HIDING the tab stops the clock, showing it starts a new one. Time in a
 *      background tab is not time spent, and counting it would replace an
 *      undercount with a worse overcount.
 *   3. `pagehide` stops the ACTIVE timer before flushing, so the last stretch
 *      is not lost on close.
 *
 * The two-second floor stays, and applies per checkpoint rather than per visit.
 ****************/

/** The screen currently being timed, so an unload can close it. */
let activeStop: (() => void) | null = null;

const CHECKPOINT_MS = 60_000;

/*
 * VISITS ARE COUNTED ONCE, AND IDLE TIME IS NOT TIME. 2026-10-05.
 *
 * Guillaume: the admin page "have been seen 11000x time, which seem a bit
 * excessive, and the time display for each does not seem realistic".
 * screen_viewed is a minute's checkpoint, so counting those counted minutes,
 * not visits; and a visible tab nobody touches (a second monitor) kept the
 * clock running for hours. A visit is now its own event, screen_opened, sent
 * once when a screen opens; and the clock stops after IDLE_MS with no input,
 * starting again at the next one.
 */
export const IDLE_MS = 5 * 60_000;
let lastInputAt = Date.now();
/** The running timer's restart, called when input returns after idling. */
let onWake: (() => void) | null = null;

export function noteInput(now = Date.now()): void {
  const wasIdle = now - lastInputAt > IDLE_MS;
  lastInputAt = now;
  if (wasIdle) onWake?.();
}

/** A notification opened this page (sw.js adds ?push=<kind>): recorded once, then the tag is taken off the address. */
export function notePushOpen(): void {
  try {
    const u = new URL(window.location.href);
    const kind = u.searchParams.get('push');
    if (!kind) return;
    u.searchParams.delete('push');
    window.history.replaceState(window.history.state, '', u.pathname + u.search + u.hash);
    track('notification_opened', kind.replace(/[^A-Za-z]/g, '').slice(0, 32));
  } catch { /* nothing to record */ }
}

if (typeof window !== 'undefined') {
  for (const ev of ['pointerdown', 'pointermove', 'keydown', 'wheel', 'scroll', 'touchstart'] as const) {
    window.addEventListener(ev, () => noteInput(), { passive: true, capture: true });
  }
}

export function startScreenTimer(name: string): () => void {
  let startedAt = Date.now();
  let stopped = false;
  track('screen_opened', name);

  // Records what has accrued and restarts the clock, so a checkpoint does not
  // double-count the stretch it just reported. Time past IDLE_MS after the
  // last input is not counted.
  const record = () => {
    const now = Date.now();
    const ms = Math.min(now, lastInputAt + IDLE_MS) - startedAt;
    startedAt = now;
    // Under two seconds is a page passed through, not a page read. Recording
    // those would bury the real reading time under navigation noise.
    if (ms >= 2000) track('screen_viewed', name, ms);
  };

  const checkpoint = setInterval(() => {
    if (!stopped && document.visibilityState === 'visible') record();
  }, CHECKPOINT_MS);

  const onVisibility = () => {
    if (stopped) return;
    if (document.visibilityState === 'hidden') record();
    else { startedAt = Date.now(); noteInput(); }
  };
  // Back from idle: the clock restarts now, not at the last checkpoint.
  const wake = () => { if (!stopped) startedAt = Date.now(); };
  onWake = wake;
  document.addEventListener('visibilitychange', onVisibility);

  const stop = () => {
    if (stopped) return;
    stopped = true;
    clearInterval(checkpoint);
    document.removeEventListener('visibilitychange', onVisibility);
    if (activeStop === stop) activeStop = null;
    if (onWake === wake) onWake = null;
    record();
  };

  activeStop = stop;
  return stop;
}

/**
 * Time on one trader's profile, reported when they leave it.
 *
 * The same shape as startScreenTimer and for the same reason -- duration is
 * only knowable at the end -- but emitting `trader_viewed`, which is what the
 * admin view reads to tell a trader somebody studied from one they scrolled
 * past. Returns the stop function so a React effect's cleanup calls it.
 */
export function startScreenTimerFor(traderId: string): () => void {
  const startedAt = Date.now();
  let stopped = false;
  return () => {
    if (stopped) return;
    stopped = true;
    const ms = Date.now() - startedAt;
    // Same two-second floor as a page view: below it, they passed through.
    if (ms >= 2000) track('trader_viewed', traderId, ms);
  };
}

// A page closed is a page left, and the timer's cleanup does not run on unload.
//
// The RUNNING timer is stopped first. Flushing alone sent the queue and left
// the current screen's time unmeasured, which is most of a visit for anyone who
// opens one page and stays on it.
if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', () => {
    activeStop?.();
    void flush();
  });
}

/****************
 * WHY A KEY WAS REFUSED, AS A CLASS. 2026-10-07.
 *
 * The exchange's words vary by venue and language; the friction panel needs
 * the handful of causes a person can act on. Only the class leaves the
 * browser, never the message (which can quote the key).
 ****************/
export type KeyErrorClass =
  | 'invalid_signature' | 'ip_whitelist' | 'permissions' | 'expired'
  | 'testnet_mismatch' | 'passphrase' | 'network' | 'unknown';

export function keyErrorClass(message?: string | null): KeyErrorClass {
  const m = (message ?? '').toLowerCase();
  if (!m) return 'unknown';
  // Never reached the exchange: not the key's fault, whatever else the text says (10-25).
  if (/network|timeout|timed out|unreachable|cannot reach|could not reach|fetch|econn|\b5\d\d\b/.test(m)) return 'network';
  if (/\bip\b|whitelist|allowlist|white list/.test(m)) return 'ip_whitelist';
  if (/signature|invalid (api )?key|api key (is )?invalid|401|unauthori[sz]ed|authenticat/.test(m)) return 'invalid_signature';
  if (/permission|not allowed|forbidden|403|trade (right|access)/.test(m)) return 'permissions';
  if (/expire/.test(m)) return 'expired';
  if (/testnet|demo|mainnet/.test(m)) return 'testnet_mismatch';
  if (/passphrase/.test(m)) return 'passphrase';

  return 'unknown';
}
